// GenericNote.tsx — currency-aware banknote colors
import { getCurrencySymbol } from "../../lib/utils";

type Props = {
  value: number | string;
  currency: "EUR" | "USD" | "GBP" | string;
  className?: string;
};

const NOTE_COLORS: Record<string, Record<number, string>> = {
  EUR:  { 5:'#8E9888', 10:'#C06040', 20:'#4E7EA6', 50:'#C49028', 100:'#8CB030', 200:'#CCA818', 500:'#9888BC' },
  USD:  { 1:'#7AAF6E', 2:'#7AAF6E', 5:'#7AAF6E', 10:'#7AAF6E', 20:'#7AAF6E', 50:'#7AAF6E', 100:'#7AAF6E' },
  GBP:  { 5:'#4E9098', 10:'#C07840', 20:'#9070A8', 50:'#B84048' },
  CHF:  { 10:'#C8A020', 20:'#A82838', 50:'#2A7848', 100:'#2450A0', 200:'#6A3818', 1000:'#503070' },
  JPY:  { 1000:'#2450A0', 5000:'#703090', 10000:'#6A3818' },
  TRY:  { 5:'#703090', 10:'#A82838', 20:'#982030', 50:'#603080', 100:'#2E7A4F', 200:'#482870' },
  MAD:  { 20:'#703090', 50:'#2E7A4F', 100:'#6A3818', 200:'#2450A0' },
  CAD:  { 5:'#2450A0', 10:'#703090', 20:'#2E7A4F', 50:'#8A1E28', 100:'#6A3818' },
  BEF:  { 50:'#6A3818', 100:'#503018', 200:'#C87820', 500:'#2450A0', 1000:'#2E7A4F', 2000:'#703090', 5000:'#482870', 10000:'#7A9830' },
}

const USE_REAL_COLORS = false

const NEUTRAL_COLOR = '#7AAF6E'
const FALLBACK_COLOR = '#A8A9AD'

const getNoteColor = (currency: string, value: number | string): string => {
  if (!USE_REAL_COLORS) return NEUTRAL_COLOR
  const v = typeof value === 'string' ? parseFloat(value) : value
  return NOTE_COLORS[currency.toUpperCase()]?.[v] ?? FALLBACK_COLOR
}

const isLight = (hex: string): boolean => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 155
}

export const GenericNote = ({ value, currency, className }: Props) => {
  const symbol = getCurrencySymbol(currency);
  const bgColor = getNoteColor(currency, value)
  const light = isLight(bgColor)
  const textColor = light ? '#1a1a1a' : '#ffffff'

  return (
    <svg viewBox="0 0 60 30" className={`${className ?? "w-full h-full"} max-w-[90px]`}>
      {/* Base */}
      <rect x="1" y="1" width="58" height="28" rx="3" fill={bgColor} stroke="rgba(0,0,0,0.25)" strokeWidth="1.5"/>

      {/* Subtle inner shine */}
      <rect x="5" y="5" width="50" height="20" rx="2" fill="white" opacity="0.2"/>

      {/* Tiny security dots */}
      <circle cx="10" cy="15" r="2.5" fill="white" opacity="0.25"/>
      <circle cx="50" cy="15" r="2.5" fill="white" opacity="0.25"/>

      {/* Main value */}
      <text
        x="30"
        y="19"
        fontSize="14"
        fontWeight="500"
        fontFamily="system-ui, sans-serif"
        letterSpacing=".7"
        textAnchor="middle"
        fill={textColor}
        stroke={light ? 'rgba(255,255,255, .7)' : 'rgba(25, 53, 24, 0.9)'}
        strokeWidth="2"
        paintOrder="stroke fill"
        opacity="0.9"
      >
        {symbol}{value}
      </text>
    </svg>
  );
};

export default GenericNote;
